// ConvertDealTypeButton — inline dropdown menu on the property workspace
// header that lets the user flip a property's deal_type in one click.
//
// Common use: user added a lot as `land`, decides to run The Velocity Play
// on it, one click flips it to `new_construction` and the workspace
// re-renders with the New Construction panel + Velocity Play calculator.

import React, { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Repeat, ChevronDown, Home as HomeIcon, Trees as TreesIcon,
  Building2, Hammer as HammerIcon, Check,
} from "lucide-react";

const DEAL_TYPES = [
  { key: "house",            label: "House",            icon: HomeIcon,   hint: "SF · condo · townhome" },
  { key: "land",             label: "Land",             icon: TreesIcon,  hint: "Raw land · vacant lot" },
  { key: "multifamily",      label: "Multifamily",      icon: Building2,  hint: "Duplex · 2-4 unit · apt" },
  { key: "new_construction", label: "New Construction", icon: HammerIcon, hint: "Build-to-sell · spec · Velocity Play" },
];

export default function ConvertDealTypeButton({ property, onConverted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const convert = async (newType) => {
    if (newType === property.deal_type) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/v3/properties/${property.id}/convert-deal-type`, {
        deal_type: newType,
      });
      const label = DEAL_TYPES.find((d) => d.key === newType)?.label || newType;
      toast.success(`Converted to ${label}`);
      onConverted?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conversion failed");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const currentLabel = DEAL_TYPES.find((d) => d.key === property.deal_type)?.label || "Unknown";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        data-testid="convert-deal-type-button"
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 hover:border-amber-300 hover:text-slate-900 shadow-sm disabled:opacity-50"
      >
        <Repeat className="w-3.5 h-3.5" />
        <span className="whitespace-nowrap">
          Type: <span className="text-amber-700">{currentLabel}</span>
        </span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1.5 w-72 rounded-xl border border-slate-200 bg-white shadow-2xl z-20 overflow-hidden"
          data-testid="convert-deal-type-menu"
        >
          <div className="px-3 py-2 border-b border-slate-100 text-[10.5px] font-bold uppercase tracking-widest text-slate-500">
            Convert Deal Type
          </div>
          {DEAL_TYPES.map(({ key, label, icon: Icon, hint }) => {
            const active = property.deal_type === key;
            return (
              <button
                key={key} type="button" onClick={() => convert(key)}
                data-testid={`convert-to-${key}`}
                className={
                  "w-full flex items-start gap-2.5 px-3 py-2.5 text-left transition-colors " +
                  (active ? "bg-amber-50" : "hover:bg-slate-50")
                }
              >
                <Icon className={"w-4 h-4 mt-0.5 shrink-0 " + (active ? "text-amber-600" : "text-slate-500")} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={"text-[13px] font-semibold " + (active ? "text-amber-900" : "text-slate-900")}>
                      {label}
                    </span>
                    {active && <Check className="w-3.5 h-3.5 text-amber-600" />}
                  </div>
                  <div className="text-[11px] text-slate-500 leading-snug">{hint}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
